import { AnalysisSetupStepTitles } from "@models/platform-analysis/entities/steps/setup";

export class AnalysisSetupStepConstants {
  static readonly MaxTemplateItemToTake = 20;
  static readonly MaxAnalysisNameLength = 90;
  static readonly SupplierMaxOtherPlatforms = 5;
}

export class AnalysisSetupStepSectionTitles {
  static readonly SupplierDetails = "Analysis details";
  static readonly AdviserDetails = "Review details";
  static readonly AdviserFamilyMembers = "Select family members and entities";
  static readonly Objectives = "Objectives";
  static readonly ClientSituation = "Current client situation and needs";
  static readonly SupplierTargetAdviser = "Who is this analysis for?";
  static readonly LoadATemplate = "Load a template";
  static readonly RateCardsTitle = "Rate cards";
  static readonly SupplierMainPlatform = "Select main platform for analysis";
  static readonly AdviserMainPlatform = "Set current platform";
  static readonly OtherPlatform = "Select other platforms";

  static readonly Details: AnalysisSetupStepTitles = {
    supplier: this.SupplierDetails,
    adviser: this.AdviserDetails,
  };
  static readonly FamilyMembers: AnalysisSetupStepTitles = {
    adviser: this.AdviserFamilyMembers,
  };
  static readonly SituationAndObjectives: AnalysisSetupStepTitles = {
    supplier: this.Objectives,
    adviser: this.ClientSituation,
  };
  static readonly TargetAdviser: AnalysisSetupStepTitles = {
    supplier: this.SupplierTargetAdviser,
  };
  static readonly LoadTemplate: AnalysisSetupStepTitles = {
    supplier: this.LoadATemplate,
    adviser: this.LoadATemplate,
  };
  static readonly RateCards: AnalysisSetupStepTitles = {
    supplier: this.RateCardsTitle,
    adviser: this.RateCardsTitle,
  };

  static readonly MainPlatform: AnalysisSetupStepTitles = {
    supplier: this.SupplierMainPlatform,
    adviser: this.AdviserMainPlatform,
  };

  static readonly OtherPlatforms: AnalysisSetupStepTitles = {
    supplier: this.OtherPlatform,
    adviser: this.OtherPlatform,
  };
}

export class AnalysisSetupStepSectionDescriptions {
  static readonly SupplierDetails =
    "Give your analysis a name and assign an owner. This will help you find the analysis in the future. Analysis names must be unique within your organisation";
  static readonly AdviserDetails =
    "Give a name to this review and assign an adviser and client to it. This will help you find this review in the future. Clients are linked to advisers, and if you cannot find one in the dropdown then try changing the adviser";
  static readonly AdviserFamilyMembers =
    "At least one family member or entity must be included in the review. You will be able to assign platform accounts to these members during the fee analysis stage. To manage family members, edit your client in the “Clients” tab of the “Suitability Reviews” page";
  static readonly Objectives =
    "Articulate the objectives of this analysis. This will appear on the final report";
  static readonly ClientSituation =
    "Articulate what this client's needs and priorities are when selecting a platform. This will appear on the report generated at the end of this review";
  static readonly SupplierTargetAdviser =
    "To provide a more personalised report, you can specify which adviser and advice practice you are running this analysis for. Their name will appear throughout the analysis. You can search the analysis by adviser name";
  static readonly LoadATemplate =
    "You can either choose each platform feature to include in your analysis one by one, or you can load from a template or analysis your firm has created";
  static readonly RateCardDescription =
    "Choose which rate cards to include in your platform fee analysis. Only rate cards set as “Available” are shown";

  static readonly SupplierMainPlatform =
    "This platform will take the primary position in your analysis";
  static readonly AdviserMainPlatform =
    "If the client has an existing platform, you can select it to give it the primary position in your analysis";

  static readonly SupplierOtherPlatform =
    "Including the main platform, up to five platforms can be included in the analysis. For audit purposes, the data in this analysis is locked to the date it was created. However, adding or changing platforms once the analysis has started will refresh all platform data to the latest version. Any rate card selections will clear";
  static readonly AdviserOtherPlatform =
    "Choose which platforms should be included in the analysis. For audit purposes, the data in this analysis is locked to the date it was created. However, adding or changing platforms once the analysis has started will refresh all platform data to the latest version. Any rate card selections will clear";

  static readonly Details: AnalysisSetupStepTitles = {
    supplier: this.SupplierDetails,
    adviser: this.AdviserDetails,
  };
  static readonly FamilyMembers: AnalysisSetupStepTitles = {
    adviser: this.AdviserFamilyMembers,
  };
  static readonly SituationAndObjectives: AnalysisSetupStepTitles = {
    supplier: this.Objectives,
    adviser: this.ClientSituation,
  };
  static readonly TargetAdviser: AnalysisSetupStepTitles = {
    supplier: this.SupplierTargetAdviser,
  };
  static readonly LoadTemplate: AnalysisSetupStepTitles = {
    supplier: this.LoadATemplate,
    adviser: this.LoadATemplate,
  };
  static readonly RateCards: AnalysisSetupStepTitles = {
    supplier: this.RateCardDescription,
    adviser: this.RateCardDescription,
  };

  static readonly MainPlatform: AnalysisSetupStepTitles = {
    supplier: this.SupplierMainPlatform,
    adviser: this.AdviserMainPlatform,
  };

  static readonly OtherPlatform: AnalysisSetupStepTitles = {
    supplier: this.SupplierOtherPlatform,
    adviser: this.AdviserOtherPlatform,
  };
}

export class AnalysisSetupStepFeatureTitles {
  static readonly SupplierFeatureStep = "Feature analysis";
  static readonly AdviserFeatureStep = "Feature review";

  static readonly FeatureStep: AnalysisSetupStepTitles = {
    supplier: this.SupplierFeatureStep,
    adviser: this.AdviserFeatureStep,
  };

  static readonly SupplierBusinessMetricStep = "Business metric analysis";
  static readonly AdviserBusinessMetricStep = "Business metric review";

  static readonly BusinessMetricStep: AnalysisSetupStepTitles = {
    supplier: this.SupplierBusinessMetricStep,
    adviser: this.AdviserBusinessMetricStep,
  };

  static readonly SupplierFeeStep = "Fee analysis";
  static readonly AdviserFeeStep = "Fee review";

  static readonly FeeStep: AnalysisSetupStepTitles = {
    supplier: this.SupplierFeeStep,
    adviser: this.AdviserFeeStep,
  };
}

export class AnalysisSetupStepDialogHeaders {
  static readonly DiscardedOneStage = "Discard stage specific data?";
  static readonly DiscardedManyStages = "Discard stages specific data?";
}

export class AnalysisSetupStepDialogBodies {
  static readonly DiscardedOneStage =
    "A stage has been removed from the analysis. Discard any existing data relating to the stage?";
  static readonly DiscardedManyStages =
    "Some stages have been removed from the analysis. Discard any existing data relating to those stages?";
}
